import { memo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { type EdgeProps, EdgeLabelRenderer, useReactFlow, getBezierPath, BaseEdge } from 'reactflow';
// Import UI Components
import { Button, Icon, Tooltip } from '@geneui/components';
// Import Context
import { usePageMode } from 'context';
// Import Enums
import { PageTypesEnum } from 'enums';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import styles from './edgeWithButtons.module.scss';

const EdgeWithButtons: React.FC<EdgeProps> = (props) => {
    const { id, style, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, markerEnd } = props;
    const { t } = useTranslation();
    const { deleteElements } = useReactFlow();
    const [isHovered, setIsHovered] = useState(false);
    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    const { pageMode } = usePageMode();
    const isViewPage = pageMode === PageTypesEnum.view;

    const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        deleteElements({ edges: [{ id }] });
    };

    return (
        <g>
            <BaseEdge style={style} path={edgePath} markerEnd={markerEnd} />

            {/* An invisible thicker line to make hovering easier for users */}
            <path
                d={edgePath}
                fill="none"
                stroke="transparent"
                strokeWidth={50}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            />

            {!isViewPage && (
                <EdgeLabelRenderer>
                    <div
                        style={{ transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)` }}
                        className={classNames(styles.buttonContainer, { [styles.visible]: isHovered })}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <Tooltip title={t(l.Delete)}>
                            <Button
                                className={styles.button}
                                color="default"
                                appearance="minimal"
                                onClick={handleDeleteClick}
                            >
                                <Icon className={styles.buttonIcon} type="bc-icon-delete-2" isFilled={true} />
                            </Button>
                        </Tooltip>
                    </div>
                </EdgeLabelRenderer>
            )}
        </g>
    );
};

export default memo(EdgeWithButtons);
